import { Avatar } from '../common';
import type { ActivityFeedEvent } from '@bbbk/schema';
import {
  groupByDay,
  groupIntoRuns,
  timeOfDay,
  emailKey,
  pageViewRow,
  actionText,
  personName,
  landingSection,
  feedDestination,
  type FeedDestination,
} from './activityFormat';
import { RoleBadge } from './tabUi';
import type { Person } from './types';

// The everyone-feed rendering — the LEDGER (docs/design/activity-feed-rethink,
// direction A, picked by Freddi 2026-07-25).
//
// Day bands, and within a day consecutive events by one person collapse into a
// RUN: identity is stated once in the run head, and the rows beneath carry only
// what happened. That replaced a row shape where the actor's email — the widest
// element on the line — repeated unchanged down the page while the part that
// actually varied was squeezed to the right.
//
// The rules the direction turns on:
//  - No wire vocabulary. Actions read as English (actionText); the record id is
//    demoted to a muted chip, because resolving it to a name ("Rosie's booking")
//    needs a server-side join the feed endpoint does not do.
//  - Kind is encoded in the MARKER. A filled dot is a change to the business's
//    data; a hollow one is presence. Before this they rendered in identical
//    grey, so the thing that altered a booking looked exactly like the thing
//    that merely happened.
//  - Every row that leads somewhere is a link to it (feedDestination). A row
//    that leads nowhere honest — a deleted record's modal, a staff member's own
//    screen — stays inert rather than pretending.
//
// The run HEAD opens the edit-user modal; the rows beneath navigate. They are
// siblings, never nested, so there is no interactive element inside another.
// An off-list actor (no allowlist Person) renders an inert head.

// One event line: time, kind marker, what happened, and the record id or dwell
// at the trailing edge. Page views and actions share the phrasing helpers with
// the per-user rail, so the two surfaces can never word the same event
// differently. `landedOn` is only ever set for a login.
function RunEventBody({ event, landedOn }: { event: ActivityFeedEvent; landedOn: string | null }) {
  const isChange = event.kind === 'action';
  const view = event.kind === 'page_view' ? pageViewRow(event) : null;
  return (
    <>
      <span className="feed-ev-time">{timeOfDay(event.at)}</span>
      <span className={'feed-dot ' + (isChange ? 'change' : 'presence')} aria-hidden="true" />
      <span className="feed-run-txt">
        {event.kind === 'login' ? (
          // Where the sign-in led. Plain "on <page>" — no separator glyph,
          // because a page's own name can contain one ("My Shifts · Today").
          <span className="feed-ev-kind">signed in{landedOn && <> on {landedOn}</>}</span>
        ) : view ? (
          <span className="feed-ev-kind">{view.text}</span>
        ) : (
          <span className="feed-ev-change">{actionText(event.label)}</span>
        )}
      </span>
      {/* Trailing metadata. Dwell does NOT sit inline behind a middle dot: a
          page's own name can already contain one, so an inline "· 40s" made one
          glyph do two jobs and the row read as two page views. Dwell and a
          record id are both references about the row, and never collide —
          dwell is page-view-only, target action-only. */}
      {view?.dwell && <span className="feed-ev-dwell">{view.dwell}</span>}
      {event.kind === 'action' && event.target && <span className="feed-ev-target">{event.target}</span>}
    </>
  );
}

// A row is a button when it leads somewhere, and a plain div otherwise. The
// title names the destination, so it is clear BEFORE clicking that a deleted
// booking lands on Bookings rather than on the booking.
function RunEvent({
  event,
  landedOn,
  onOpen,
}: {
  event: ActivityFeedEvent;
  landedOn: string | null;
  onOpen: (dest: FeedDestination) => void;
}) {
  const dest = feedDestination(event);
  const body = <RunEventBody event={event} landedOn={landedOn} />;
  if (!dest) return <div className="feed-run-ev">{body}</div>;
  return (
    <button type="button" className="feed-run-ev clickable" onClick={() => onOpen(dest)} title={destinationHint(dest)}>
      {body}
    </button>
  );
}

function destinationHint(dest: NonNullable<FeedDestination>): string {
  switch (dest.kind) {
    case 'booking':
      return 'Open this booking';
    case 'owner':
      return 'Open this household';
    case 'helper':
      return 'Open this helper';
    case 'helperLeave':
      return "Open this helper's leave";
    default:
      return 'Go to this page';
  }
}

export interface ActivityTimelineProps {
  events: ActivityFeedEvent[];
  // Current allowlist person per lowercased actor email — drives the role pill
  // and which run heads are clickable (off-list actors resolve to undefined).
  peopleByKey: Record<string, Person>;
  // Open the edit-user modal for an allowlist actor (run-head click).
  onActorClick: (email: string) => void;
  // Follow a row to where it leads — a section, or a record's modal.
  onOpen: (dest: FeedDestination) => void;
}

export function ActivityTimeline({ events, peopleByKey, onActorClick, onOpen }: ActivityTimelineProps) {
  return (
    <>
      {groupByDay(events).map(([day, rows]) => {
        // The landing-page fallback searches the whole DAY, not one run: a run
        // ends the moment another actor's event interleaves, which would hide a
        // knowable landing page. Runs are slices of `rows`, so each event's
        // position in the day is looked up rather than recomputed.
        const dayIndex = new Map(rows.map((e, i) => [e.id, i]));
        return (
        <section key={day} className="feed-day-group">
          <div className="feed-day">{day}</div>
          {groupIntoRuns(rows).map((run) => {
            const person = peopleByKey[emailKey(run.actor)];
            // Oldest → newest, though the run's events are newest-first: a head
            // reading "14:02 – 13:51" would be backwards.
            const span = `${timeOfDay(run.from)} – ${timeOfDay(run.to)}`;
            // The name is the local part, so two different addresses can render
            // identically ("alex@vendor-a.ie" and "alex@vendor-b.ie" are both
            // "Alex"). An on-list head carries the full address in its Edit
            // title; an OFF-LIST head had none at all, which made historical
            // attribution ambiguous in an audit trail (Codex, PR #149) — so it
            // carries the address too.
            const head = (
              <>
                <Avatar name={run.actor} id={run.actor} size={26} />
                <span className="feed-run-who" title={run.actor}>
                  {personName(run.actor)}
                </span>
                <RoleBadge role={person?.role ?? null} />
                {!person && <span className="feed-run-addr">{run.actor}</span>}
                <span className="feed-run-span">{span}</span>
              </>
            );
            // Key on the newest event's id — stable, and unique even when one
            // person has several runs in a day.
            return (
              <div key={run.events[0]!.id} className="feed-run">
                {person ? (
                  <button
                    type="button"
                    className="feed-run-head clickable"
                    onClick={() => onActorClick(person.email)}
                    title={`Edit ${person.email}`}
                  >
                    {head}
                  </button>
                ) : (
                  <div className="feed-run-head">{head}</div>
                )}
                {run.events.map((e) => (
                  <RunEvent
                    key={e.id}
                    event={e}
                    landedOn={e.kind === 'login' ? landingSection(rows, dayIndex.get(e.id) ?? -1) : null}
                    onOpen={onOpen}
                  />
                ))}
              </div>
            );
          })}
        </section>
        );
      })}
    </>
  );
}
